import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { KTCard } from '../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../_metronic/helpers/components/KTCardBody';
import { storeActivityIndicator } from '../../redux/reducers/authSlice';
import CustomTextInput from '../../components/CustomTextInput';
import { TakasItem, VardiyaService } from '../../services/VardiyaService';
import { izinDurumSinifi, IZIN_BEKLIYOR } from '../../services/IzinService';
import { vardiyaRengi } from '../../helpers/vardiyaDesen';

interface Props {
    liste: TakasItem[]
    kodlar: string[]
    kararYetkisi: boolean
    yenile: () => Promise<void>
}

/**
 * Vardiya Çizelgesi > Gün Değişimi İstekleri (İK Faz 3, 03.11.2026). Personel mobilden ister; onaylanınca o güne günlük değişiklik
 * yazılır (takasta iki kişiye), faturalanmamış mesai kayıtları yeniden hesaplanır. Red nedeni zorunlu, personele bildirim gider.
 */
const TakasIstekleri = ({ liste, kodlar, kararYetkisi, yenile }: Props) => {
    const dispatch = useDispatch();
    const [reddedilen, setReddedilen] = useState<string | undefined>(undefined);
    const [neden, setNeden] = useState<string | undefined>(undefined);

    const karar = async (t: TakasItem, onay: boolean) => {
        try {
            if (!onay && (!neden || neden.trim() == '')) {
                toast.error('Red nedenini yazınız.');
                return;
            }
            dispatch(storeActivityIndicator(true));
            const r = await VardiyaService.takasKarar(t.takasId, onay, onay ? null : neden!.trim());
            if (!r.isSuccess) {
                toast.error(r.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }
            toast.success(onay ? 'Gün değişimi onaylandı; çizelgeye yazıldı.' : 'Gün değişimi reddedildi.');
            setReddedilen(undefined);
            setNeden(undefined);
            await yenile();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const kod = (k: string | null | undefined, saat?: string | null) =>
        k ? <span className='badge ebbe-status-badge' title={saat ?? undefined} style={vardiyaRengi(k, kodlar)}>{k}</span> : <span className='text-muted'>–</span>;

    return (
        <KTCard className='mb-3'>
            <KTCardBody className='py-2'>
                <div className='table-responsive new-table-container'>
                    <table className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                        <thead>
                            <tr className='text-start text-muted fw-bolder text-uppercase'>
                                <th>Personel</th><th>Gün</th><th>Şu An</th><th>İstenen</th><th>Takas</th><th>Açıklama</th><th>Durum</th><th>Karar</th>
                            </tr>
                        </thead>
                        <tbody className='text-gray-600 fw-bold'>
                            {liste.length == 0 && <tr><td className="text-center" colSpan={99}>İstek yok. Personel mobil uygulamada Vardiyam ekranından gün değişimi ister.</td></tr>}
                            {
                                liste.map(t =>
                                    <tr key={t.takasId}>
                                        <td>{t.personelAdi}<br /><span className='text-muted fs-8'>{t.olusturmaZamani}</span></td>
                                        <td>{t.tarihStr}</td>
                                        <td>{kod(t.mevcutKod, t.mevcutSaat)}</td>
                                        <td>{kod(t.istenenKod, t.istenenSaat)}</td>
                                        <td>{t.karsiPersonelAdi ? <React.Fragment>{t.karsiPersonelAdi} {kod(t.karsiMevcutKod)}</React.Fragment> : ''}</td>
                                        <td style={{ whiteSpace: 'normal' }}>{t.aciklama}</td>
                                        <td>
                                            <span className={'badge ebbe-status-badge ' + izinDurumSinifi(t.durum)}>{t.durumAdi}</span>
                                            {
                                                t.durum == IZIN_BEKLIYOR && kararYetkisi && reddedilen != t.takasId && (
                                                    <div className='d-flex gap-1 mt-1'>
                                                        <a className='btn btn-sm btn-light-success py-1 px-2' onClick={() => karar(t, true)}>Onayla</a>
                                                        <a className='btn btn-sm btn-light-danger py-1 px-2' onClick={() => { setReddedilen(t.takasId); setNeden(undefined); }}>Reddet</a>
                                                    </div>
                                                )
                                            }
                                            {
                                                reddedilen == t.takasId && (
                                                    <div className='mt-2' style={{ minWidth: 220 }}>
                                                        <CustomTextInput labelText='Red nedeni' isLabelRequired value={neden} setValue={setNeden} />
                                                        <div className='d-flex gap-1 mt-1'>
                                                            <a className='btn btn-sm btn-danger py-1 px-2' onClick={() => karar(t, false)}>Reddet</a>
                                                            <a className='btn btn-sm btn-light py-1 px-2' onClick={() => setReddedilen(undefined)}>Vazgeç</a>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </td>
                                        <td>
                                            {t.kararVeren && <React.Fragment>{t.kararVeren}<br /><span className='text-muted fs-8'>{t.kararZamani}</span></React.Fragment>}
                                            {t.redNedeni && <div className='text-danger fs-8' style={{ whiteSpace: 'normal' }}>{t.redNedeni}</div>}
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
                <div className="text-muted fs-7 py-3">
                    Son 30 gün ve ileri tarihli istekler. Onaylanınca istek sahibine istenen vardiya, takasta karşı personele istek sahibinin o günkü vardiyası günlük değişiklik olarak yazılır.
                    {' '}Mobilde onaylayan: personel kartındaki izin onaylayıcısı, yoksa görevinde "İzin Onay" olanlar.
                </div>
            </KTCardBody>
        </KTCard>
    );
}

export default TakasIstekleri;
